import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ToasterService } from '../toaster/toaster.service';
import { PortraitService } from '../portraits/portrait.service';
import { SoundService } from '../sound/sound.service';
import { IdentityService } from '../identity/identity.service';
import { Achievement, AchievementEventContext, AchievementUnlockedResult, UserAchievement } from '../../models/achievements.model';

@Injectable({
  providedIn: 'root',
})
export class AchievementService {
  private http = inject(HttpClient);
  private toaster = inject(ToasterService);
  private portraitService = inject(PortraitService);
  private sound = inject(SoundService);
  private identity = inject(IdentityService);

  readonly unlockedReward = signal<any | null>(null);
  readonly latestUnlockedAchievement = signal<AchievementUnlockedResult | null>(null);
  readonly unlockedAchievementsList = signal<AchievementUnlockedResult[]>([]);

  /**
   * Process incoming SignalR award payload from recvAward event.
   * @param award Award payload from server
   */
  public handleAward(award: any): void {
    console.log('Processing award in AchievementService:', award);
    const awardType = (award.type || award.rewardType || 'icon').toString().toLowerCase();

    switch (awardType) {
      case 'portrait':
        this.processPortraitAward(award);
        break;
      case 'nameplate':
        this.processNameplateAward(award);
        break;
      case 'namecolor':
        this.processNamecolorAward(award);
        break;
      case 'icon':
      default:
        this.processIconAward(award);
        break;
    }
  }

  /**
   * Process incoming SignalR AchievementUnlocked event payload.
   * @param reward Unlocked achievement result
   */
  public handleAchievementUnlocked(reward: AchievementUnlockedResult): void {
    console.log('Processing AchievementUnlocked event:', reward);
    this.sound.playChime();
    this.latestUnlockedAchievement.set(reward);
    this.unlockedAchievementsList.update(list => [reward, ...list]);
  }

  private processPortraitAward(award: any): void {
    const portraits = this.portraitService.portraits();
    const found = portraits.find((p: any) => p.id == award.iconId);
    const imageUrl = found ? `https://static.h5arena.com/images/avatars/${found.fileName}` : 'images/featured/elite_subscription.png';
    const title = award.name || (found ? `Portrait ${found.fileName}` : 'New Portrait');
    const desc = award.description || 'Congratulations! You unlocked a new profile portrait.';

    this.toaster.show(`Congratulations! You unlocked: ${title}`, 'success', 5000, undefined, undefined, imageUrl);
    this.unlockedReward.set({
      id: `award-portrait-${award.iconId}`,
      name: title,
      rarity: 'Legendary',
      imageUrl: imageUrl,
      description: desc
    });
  }

  private processNameplateAward(award: any): void {
    const title = award.name || 'New Nameplate';
    const desc = award.description || 'Congratulations! You unlocked a new profile nameplate.';
    this.toaster.success(`Congratulations! You unlocked: ${title}`);
    this.unlockedReward.set({
      id: `award-nameplate-${award.iconId}`,
      name: title,
      rarity: 'Legendary',
      imageUrl: 'images/featured/elite_subscription.png',
      description: desc
    });
  }

  private processNamecolorAward(award: any): void {
    const title = award.name || 'New Name Color';
    const desc = award.description || `Congratulations! You unlocked a new profile name color (${award.iconId}).`;
    this.toaster.success(`Congratulations! You unlocked: ${title}`);
    this.unlockedReward.set({
      id: `award-namecolor-${award.iconId}`,
      name: title,
      rarity: 'Legendary',
      imageUrl: 'images/featured/elite_subscription.png',
      description: desc
    });
  }

  private processIconAward(award: any): void {
    this.http.get<any[]>(environment.platformUrl + 'api/icon/list').subscribe({
      next: (icons) => {
        const found = icons.find((icon: any) => icon.id === award.iconId);
        const iconUrl = found ? `https://static.h5arena.com/images/icons/${found.fileName}` : undefined;
        const awardName = award.name || (found && found.description ? found.description : `Icon ID ${award.iconId}`);
        const msg = award.description || (found && found.description
          ? `Congratulations! You have been awarded the "${found.description}" icon!`
          : `Congratulations! You have been awarded a new icon (${awardName})!`);

        this.toaster.show(msg, 'success', 5000, undefined, undefined, iconUrl);

        this.unlockedReward.set({
          id: `award-icon-${award.iconId}`,
          name: awardName,
          rarity: 'Legendary',
          imageUrl: iconUrl || 'images/featured/elite_subscription.png',
          description: msg
        });
      },
      error: (err) => {
        console.error('Failed to load icons for award toaster', err);
        const awardName = award.name || `New Icon (ID: ${award.iconId})`;
        const msg = award.description || `Congratulations! You have been awarded a new icon (ID: ${award.iconId})!`;
        this.toaster.success(msg);

        this.unlockedReward.set({
          id: `award-icon-${award.iconId}`,
          name: awardName,
          rarity: 'Legendary',
          imageUrl: 'images/featured/elite_subscription.png',
          description: msg
        });
      }
    });
  }

  /**
   * Fetch all active achievement definitions from backend.
   */
  public getAchievements(): Observable<Achievement[]> {
    return this.http.get<Achievement[]>(environment.platformUrl + 'api/achievements');
  }

  /**
   * Fetch achievement progress for a user.
   * @param userId Optional user ID; defaults to current authenticated user ID.
   */
  public getUserAchievements(userId?: string): Observable<UserAchievement[]> {
    const targetUserId = userId || this.identity.getClaims()?.sub || this.identity.getClaims()?.nameid || '';
    return this.http.get<UserAchievement[]>(environment.platformUrl + `api/achievements/user/${targetUserId}`);
  }

  /**
   * Perform daily check-in to trigger login achievement evaluation.
   */
  public dailyCheckin(): Observable<any> {
    return this.http.post<any>(environment.platformUrl + 'api/achievements/checkin', {});
  }

  /**
   * Evaluate a custom achievement event.
   * @param context Achievement event context.
   */
  public evaluateAchievementEvent(context: AchievementEventContext): Observable<any> {
    return this.http.post<any>(environment.platformUrl + 'api/achievements/evaluate', context);
  }
}
